import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { EmptyState } from "@/shared/components/EmptyState";
import { listAccessActivityApi } from "@/portals/caregiver/api/caregiverApi";
import { toastError } from "@/shared/lib/toast";
import type { AccessActivityEvent } from "@/shared/types/caregiverPortal";

function formatTimestamp(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  const datePart = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(parsed);
  const timePart = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(parsed);
  return `${datePart}, ${timePart}`;
}

function dotColorClass(kind: AccessActivityEvent["kind"]): string {
  if (kind === "sent") return "bg-[#0A2947]";
  return "bg-[#F36F8F]";
}

export function AccessActivityView() {
  const [events, setEvents] = useState<AccessActivityEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    listAccessActivityApi().then(({ data, error: fetchError }) => {
      if (cancelled) return;
      if (fetchError) {
        setError(fetchError);
        toastError(fetchError);
        return;
      }
      setEvents(data ?? []);
      setError(null);
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-5 lg:px-6 lg:py-6">
      <div className="w-full">
        <section className="min-h-[420px] w-full overflow-hidden rounded-2xl border border-[#DDE1E8] bg-white">
          <div className="border-b border-[#E9EBEF] bg-[#FAFAFB] px-5 py-4 lg:px-6">
            <p className="text-sm font-extrabold text-[#0A2947]">Request Timeline</p>
            <p className="mt-0.5 text-xs text-[#8A919E]">Your latest patient-access updates appear here.</p>
          </div>

          <div className="px-5 py-6 lg:px-8 lg:py-7">
            {isLoading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : error ? (
              <p className="py-12 text-center text-sm font-semibold text-[#E4002B]">{error}</p>
            ) : events.length === 0 ? (
              <EmptyState
                className="py-12"
                icon={<Icon icon="lucide:history" className="h-10 w-10 text-[#9CA3AF]" />}
                title="No activity yet"
                description="Requests you send, and how they're resolved, will show up here."
              />
            ) : (
              <div className="w-full max-w-[900px]">
                {events.map((event, index) => (
                  <div key={event.id} className="relative flex gap-4 pb-9 last:pb-0">
                    {index < events.length - 1 && (
                      <span className="absolute left-[5px] top-3 h-full w-px bg-[#E5E7EB]" />
                    )}
                    <span className={`relative z-10 mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${dotColorClass(event.kind)}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#0A2947]">{formatTimestamp(event.timestamp)}</p>
                      <p className="mt-1 text-sm leading-6 text-[#374151]">{event.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
